import React, { useState, useEffect } from 'react';
import './createEdizioneModal.css';

const CreateEdizioneModal = ({ isOpen, onClose, onSubmit, editingEdizione, torneiDisponibili, selectedTorneo }) => {
  const [formData, setFormData] = useState({
    torneo: '',
    edizione: '',
    luogo: '',
    quota: '',
    dataInizio: '',
    dataFine: '',
    categoria: '',
    censiti: '',
    chiusuraIscrizioni: '',
    chiusuraModifiche: '',
    cibo: false,
    preiscrizione: false,
    locandina: null
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Mapping categorie: display -> valore database
  const categorieMapping = {
    "L/C": "lc",
    "E/G": "eg",
    "R/S": "rs",
    "CoCa": "coca",
    "R/S CoCa": "rsc",
    "L/C, E/G": "lc,eg",
    "E/G, R/S": "eg,rs",
    "R/S, CoCa": "rs,coca",
    "Tutte": "all"
  };

  // Mapping inverso per visualizzare la categoria corretta nel dropdown durante la modifica
  const categorieReverseMapping = Object.fromEntries(
    Object.entries(categorieMapping).map(([k, v]) => [v, k])
  );

  const categorieDisponibili = [
    "L/C",
    "E/G",
    "R/S",
    "CoCa",
    "R/S CoCa",
    "L/C, E/G",
    "E/G, R/S",
    "R/S, CoCa",
    "Tutte"
  ];

  const censitiOptions = [
    { label: "Si", value: true },
    { label: "No", value: false }
  ];

  // Reset form quando il modal si apre
  useEffect(() => {
    if (isOpen) {
      if (editingEdizione) {
        // Modalità modifica - precompila i campi
        // Converte i valori del database in valori di visualizzazione
        const displayCategoria = categorieReverseMapping[editingEdizione.categoria] || editingEdizione.categoria || '';
        const displayCensiti = editingEdizione.censiti === 'true' || editingEdizione.censiti === true ? 'Si' : 'No';

        setFormData({
          torneo: editingEdizione.torneo || '',
          edizione: editingEdizione.edizione || '',
          luogo: editingEdizione.luogo || '',
          quota: editingEdizione.quota || '',
          dataInizio: editingEdizione.dataInizio || '',
          dataFine: editingEdizione.dataFine || '',
          categoria: displayCategoria,
          censiti: displayCensiti,
          chiusuraIscrizioni: editingEdizione.chiusuraIscrizioni || '',
          chiusuraModifiche: editingEdizione.chiusuraModifiche || '',
          cibo: editingEdizione.cibo || false,
          preiscrizione: editingEdizione.preiscrizione || false,
          locandina: editingEdizione.locandina || null
        });
        // Se c'è una locandina esistente, mostrala come anteprima
        setImagePreview(editingEdizione.locandina || null);
      } else {
        // Modalità creazione - campi vuoti (precompila torneo se selectedTorneo è presente)
        setFormData({
          torneo: selectedTorneo?.nome || '',
          edizione: '',
          luogo: '',
          quota: '',
          dataInizio: '',
          dataFine: '',
          categoria: '',
          censiti: 'Si',
          chiusuraIscrizioni: '',
          chiusuraModifiche: '',
          cibo: false,
          preiscrizione: false,
          locandina: null
        });
        setImagePreview(null);
      }
      setErrors({});
      setIsSubmitting(false);
      setIsClosing(false);
    }
  }, [isOpen, editingEdizione, selectedTorneo]);

  // Gestione ESC key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen && !isSubmitting) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isSubmitting]);

  // Funzione per calcolare le date correlate
  const calcDateCorrelate = (dataInizio) => {
    if (!dataInizio) return {};

    const data = new Date(dataInizio);

    // Data fine = data inizio
    const dataFine = dataInizio;

    // Chiusura iscrizioni = 4 giorni prima. "YYYY-MM-DD" si legge come mezzanotte UTC, quindi si conta in UTC: con setDate (ora locale) un intervallo a cavallo del cambio d'ora finiva alle 23 UTC del giorno prima, e toISOString restituiva la data sbagliata di un giorno.
    const chiusuraIscr = new Date(data);
    chiusuraIscr.setUTCDate(chiusuraIscr.getUTCDate() - 4);
    const chiusuraIscrizioni = chiusuraIscr.toISOString().split('T')[0];

    // Chiusura modifiche = 2 giorni prima
    const chiusuraMod = new Date(data);
    chiusuraMod.setUTCDate(chiusuraMod.getUTCDate() - 2);
    const chiusuraModifiche = chiusuraMod.toISOString().split('T')[0];

    return { dataFine, chiusuraIscrizioni, chiusuraModifiche };
  };

  // Gestione cambio data inizio
  const handleDataInizioChange = (value) => {
    const dateCorrelate = calcDateCorrelate(value);
    setFormData(prev => ({
      ...prev,
      dataInizio: value,
      ...dateCorrelate
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Rimuovi errore quando l'utente inizia a digitare
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Gestione upload immagine locandina
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validazione tipo file
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setErrors(prev => ({
        ...prev,
        locandina: 'Formato non supportato. Usa JPG, PNG, GIF o WebP.'
      }));
      return;
    }

    // Validazione dimensione (max 2MB)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setErrors(prev => ({
        ...prev,
        locandina: 'L\'immagine non deve superare i 2MB.'
      }));
      return;
    }

    // Converti in base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setFormData(prev => ({
        ...prev,
        locandina: base64String
      }));
      setImagePreview(base64String);
      // Rimuovi eventuali errori
      if (errors.locandina) {
        setErrors(prev => ({
          ...prev,
          locandina: ''
        }));
      }
    };
    reader.onerror = () => {
      setErrors(prev => ({
        ...prev,
        locandina: 'Errore durante la lettura del file.'
      }));
    };
    reader.readAsDataURL(file);
  };

  // Rimuovi immagine locandina
  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      locandina: null
    }));
    setImagePreview(null);
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validazioni obbligatorie
    if (!formData.torneo.trim()) {
      newErrors.torneo = 'Il torneo è obbligatorio';
    }
    
    if (!formData.edizione.trim()) {
      newErrors.edizione = 'L\'edizione è obbligatoria';
    } else if (formData.edizione.trim().length < 2) {
      newErrors.edizione = 'L\'edizione deve contenere almeno 2 caratteri';
    } else if (formData.edizione.trim().length > 10) {
      // la colonna in database e' varchar(10): oltre, il server rifiuta la creazione
      newErrors.edizione = 'L\'edizione non può superare i 10 caratteri';
    }
    
    if (!formData.luogo.trim()) {
      newErrors.luogo = 'Il luogo è obbligatorio';
    } else if (formData.luogo.trim().length < 3) {
      newErrors.luogo = 'Il luogo deve contenere almeno 3 caratteri';
    }
    
    if (!formData.quota.trim()) {
      newErrors.quota = 'La quota è obbligatoria';
    }
    
    if (!formData.dataInizio) {
      newErrors.dataInizio = 'La data di inizio è obbligatoria';
    }
    
    if (!formData.dataFine) {
      newErrors.dataFine = 'La data di fine è obbligatoria';
    }
    
    if (!formData.categoria) {
      newErrors.categoria = 'La categoria è obbligatoria';
    }
    
    if (!formData.chiusuraIscrizioni) {
      newErrors.chiusuraIscrizioni = 'La data di chiusura iscrizioni è obbligatoria';
    }
    
    if (!formData.chiusuraModifiche) {
      newErrors.chiusuraModifiche = 'La data di chiusura modifiche è obbligatoria';
    }

    // Validazioni logiche date
    if (formData.dataInizio && formData.dataFine) {
      const dataInizio = new Date(formData.dataInizio);
      const dataFine = new Date(formData.dataFine);
      
      if (dataFine < dataInizio) {
        newErrors.dataFine = 'La data di fine deve essere successiva alla data di inizio';
      }
    }

    if (formData.chiusuraIscrizioni && formData.dataInizio) {
      const chiusuraIscrizioni = new Date(formData.chiusuraIscrizioni);
      const dataInizio = new Date(formData.dataInizio);
      
      if (chiusuraIscrizioni >= dataInizio) {
        newErrors.chiusuraIscrizioni = 'La chiusura iscrizioni deve essere precedente all\'inizio del torneo';
      }
    }

    if (formData.chiusuraModifiche && formData.chiusuraIscrizioni) {
      const chiusuraModifiche = new Date(formData.chiusuraModifiche);
      const chiusuraIscrizioni = new Date(formData.chiusuraIscrizioni);
      
      if (chiusuraModifiche <= chiusuraIscrizioni) {
        newErrors.chiusuraModifiche = 'La chiusura modifiche deve essere successiva alla chiusura iscrizioni';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Mappa la categoria dal valore di visualizzazione al valore del database
      const mappedCategoria = categorieMapping[formData.categoria] || formData.categoria;

      // Mappa il censimento dal valore di visualizzazione a true/false (stringa)
      const censitoOption = censitiOptions.find(opt => opt.label === formData.censiti);
      const mappedCensiti = censitoOption ? String(censitoOption.value) : 'true';

      // Rimuovi il prefisso data:image/...;base64, dalla locandina
      let locandinaBase64 = formData.locandina;
      if (locandinaBase64 && locandinaBase64.includes(',')) {
        locandinaBase64 = locandinaBase64.split(',')[1];
      }

      // Prepara i dati per l'invio
      const edizioneData = {
        torneo: formData.torneo,
        edizione: formData.edizione.trim(),
        luogo: formData.luogo.trim(),
        quota: formData.quota.trim(),
        dataInizio: formData.dataInizio,
        dataFine: formData.dataFine,
        locandina: locandinaBase64,
        categoria: mappedCategoria,
        censiti: mappedCensiti,
        chiusuraIscrizioni: formData.chiusuraIscrizioni,
        chiusuraModifiche: formData.chiusuraModifiche,
        cibo: formData.cibo,
        preiscrizione: formData.preiscrizione,
        avvio: null,
        chiusura: null,
        foto: null,
        status: 'draft'
      };

      onSubmit(edizioneData);
      handleClose();
    } catch (error) {
      console.error('Errore durante la creazione:', error);
      setErrors({ submit: 'Errore durante la creazione dell\'edizione. Riprova.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 200);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className={`modal-overlay ${isClosing ? 'closing' : ''}`}
      onClick={handleOverlayClick}
    >
      <div className="modal-container modal-large">
        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">
            <i className={`fas ${editingEdizione ? 'fa-edit' : 'fa-calendar-plus'}`}></i>
            {editingEdizione ? 'Modifica Edizione' : 'Nuova Edizione'}
          </h2>
          <button 
            className="modal-close-btn"
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label="Chiudi modal"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Corpo */}
        <div className="modal-body">
          <p className="modal-description">
            {editingEdizione 
              ? 'Modifica i dettagli di questa edizione. Le modifiche saranno salvate immediatamente.'
              : 'Crea una nuova edizione di torneo inserendo tutte le informazioni necessarie. Potrai modificare questi dettagli anche successivamente.'
            }
          </p>

          <form onSubmit={handleSubmit} className="modal-form modal-form-grid-3col">
            {/* Riga 1: Torneo, Edizione, Luogo */}
            <div className="form-row-3">
              <div className={`form-group ${errors.torneo ? 'error' : ''}`}>
                <label htmlFor="torneo">
                  <i className="fas fa-trophy"></i>
                  Torneo <span className="required">*</span>
                </label>
                <select
                  id="torneo"
                  value={formData.torneo}
                  onChange={(e) => handleInputChange('torneo', e.target.value)}
                  disabled={isSubmitting}
                >
                  <option value="">Seleziona un torneo</option>
                  {torneiDisponibili && torneiDisponibili.map((torneo) => (
                    <option key={torneo} value={torneo}>{torneo}</option>
                  ))}
                </select>
                {errors.torneo && (
                  <span className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    {errors.torneo}
                  </span>
                )}
              </div>

              <div className={`form-group ${errors.edizione ? 'error' : ''}`}>
                <label htmlFor="edizione">
                  <i className="fas fa-medal"></i>
                  Edizione <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="edizione"
                  value={formData.edizione}
                  onChange={(e) => handleInputChange('edizione', e.target.value)}
                  placeholder="Es. XV"
                  disabled={isSubmitting}
                  maxLength={10}
                />
                {errors.edizione && (
                  <span className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    {errors.edizione}
                  </span>
                )}
              </div>

              <div className={`form-group ${errors.luogo ? 'error' : ''}`}>
                <label htmlFor="luogo">
                  <i className="fas fa-map-marker-alt"></i>
                  Luogo <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="luogo"
                  value={formData.luogo}
                  onChange={(e) => handleInputChange('luogo', e.target.value)}
                  placeholder="Es. Parco dei Cedri, Bologna"
                  disabled={isSubmitting}
                  maxLength={200}
                />
                {errors.luogo && (
                  <span className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    {errors.luogo}
                  </span>
                )}
              </div>
            </div>

            {/* Riga 2: Quota, Locandina. La caparra non sta nel modulo: in database e' la chiave verso i dati di pagamento (intestatario, IBAN...), non un importo, e l'importo che si scriveva qui faceva fallire la creazione. */}
            <div className="form-row">
              <div className={`form-group ${errors.quota ? 'error' : ''}`}>
                <label htmlFor="quota">
                  <i className="fas fa-euro-sign"></i>
                  Quota <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="quota"
                  value={formData.quota}
                  onChange={(e) => handleInputChange('quota', e.target.value)}
                  placeholder="Es. 25€ o Offerta libera"
                  disabled={isSubmitting}
                  maxLength={50}
                />
                {errors.quota && (
                  <span className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    {errors.quota}
                  </span>
                )}
              </div>

              <div className={`form-group ${errors.locandina ? 'error' : ''}`}>
                <label htmlFor="locandina">
                  <i className="fas fa-image"></i>
                  Locandina
                </label>
                <div className="locandina-upload-container locandina-compact">
                  {imagePreview ? (
                    <div className="locandina-preview-compact">
                      <img src={imagePreview} alt="Anteprima locandina" />
                      <button
                        type="button"
                        className="locandina-remove-btn-compact"
                        onClick={handleRemoveImage}
                        disabled={isSubmitting}
                        title="Rimuovi"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ) : (
                    <div className="locandina-upload-area-compact">
                      <input
                        type="file"
                        id="locandina"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleImageChange}
                        disabled={isSubmitting}
                        className="locandina-input"
                      />
                      <label htmlFor="locandina" className="locandina-upload-label-compact">
                        <i className="fas fa-cloud-upload-alt"></i>
                        <small>Max 2MB</small>
                      </label>
                    </div>
                  )}
                </div>
                {errors.locandina && (
                  <span className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    {errors.locandina}
                  </span>
                )}
              </div>
            </div>

            {/* Riga 3: Data Inizio, Data Fine, Categoria */}
            <div className="form-row-3">
              <div className={`form-group ${errors.dataInizio ? 'error' : ''}`}>
                <label htmlFor="dataInizio">
                  <i className="fas fa-calendar-alt"></i>
                  Data Inizio <span className="required">*</span>
                </label>
                <input
                  type="date"
                  id="dataInizio"
                  value={formData.dataInizio}
                  onChange={(e) => handleDataInizioChange(e.target.value)}
                  disabled={isSubmitting}
                />
                {errors.dataInizio && (
                  <span className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    {errors.dataInizio}
                  </span>
                )}
              </div>

              <div className={`form-group ${errors.dataFine ? 'error' : ''}`}>
                <label htmlFor="dataFine">
                  <i className="fas fa-calendar-alt"></i>
                  Data Fine <span className="required">*</span>
                </label>
                <input
                  type="date"
                  id="dataFine"
                  value={formData.dataFine}
                  onChange={(e) => handleInputChange('dataFine', e.target.value)}
                  disabled={isSubmitting}
                />
                {errors.dataFine && (
                  <span className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    {errors.dataFine}
                  </span>
                )}
              </div>

              <div className={`form-group ${errors.categoria ? 'error' : ''}`}>
                <label htmlFor="categoria">
                  <i className="fas fa-users"></i>
                  Categoria <span className="required">*</span>
                </label>
                <select
                  id="categoria"
                  value={formData.categoria}
                  onChange={(e) => handleInputChange('categoria', e.target.value)}
                  disabled={isSubmitting}
                >
                  <option value="">Seleziona</option>
                  {categorieDisponibili.map((categoria) => (
                    <option key={categoria} value={categoria}>{categoria}</option>
                  ))}
                </select>
                {errors.categoria && (
                  <span className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    {errors.categoria}
                  </span>
                )}
              </div>
            </div>

            {/* Riga 4: Chiusura Iscrizioni, Chiusura Modifiche, Censiti */}
            <div className="form-row-3">
              <div className={`form-group ${errors.chiusuraIscrizioni ? 'error' : ''}`}>
                <label htmlFor="chiusuraIscrizioni">
                  <i className="fas fa-clock"></i>
                  Chiusura Iscrizioni <span className="required">*</span>
                </label>
                <input
                  type="date"
                  id="chiusuraIscrizioni"
                  value={formData.chiusuraIscrizioni}
                  onChange={(e) => handleInputChange('chiusuraIscrizioni', e.target.value)}
                  disabled={isSubmitting}
                />
                {errors.chiusuraIscrizioni && (
                  <span className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    {errors.chiusuraIscrizioni}
                  </span>
                )}
              </div>

              <div className={`form-group ${errors.chiusuraModifiche ? 'error' : ''}`}>
                <label htmlFor="chiusuraModifiche">
                  <i className="fas fa-edit"></i>
                  Chiusura Modifiche <span className="required">*</span>
                </label>
                <input
                  type="date"
                  id="chiusuraModifiche"
                  value={formData.chiusuraModifiche}
                  onChange={(e) => handleInputChange('chiusuraModifiche', e.target.value)}
                  disabled={isSubmitting}
                />
                {errors.chiusuraModifiche && (
                  <span className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    {errors.chiusuraModifiche}
                  </span>
                )}
              </div>

              <div className={`form-group ${errors.censiti ? 'error' : ''}`}>
                <label htmlFor="censiti">
                  <i className="fas fa-id-card"></i>
                  Censiti <span className="required">*</span>
                </label>
                <select
                  id="censiti"
                  value={formData.censiti}
                  onChange={(e) => handleInputChange('censiti', e.target.value)}
                  disabled={isSubmitting}
                >
                  {censitiOptions.map((option) => (
                    <option key={option.label} value={option.label}>{option.label}</option>
                  ))}
                </select>
                {errors.censiti && (
                  <span className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    {errors.censiti}
                  </span>
                )}
              </div>
            </div>

            {/* Riga 5: Opzioni checkbox */}
            <div className="form-row-3">
              <div className="form-group form-group-checkbox-with-desc">
                <label>
                  <input
                    type="checkbox"
                    id="cibo"
                    checked={formData.cibo}
                    onChange={(e) => handleInputChange('cibo', e.target.checked)}
                    disabled={isSubmitting}
                  />
                  <i className="fas fa-utensils"></i>
                  Campi cibo
                </label>
                <small className="checkbox-description">
                  Mostra campi per accompagnatori, vegetariani, vegani, celiaci e intolleranze
                </small>
              </div>

              <div className="form-group form-group-checkbox-with-desc">
                <label>
                  <input
                    type="checkbox"
                    id="preiscrizione"
                    checked={formData.preiscrizione}
                    onChange={(e) => handleInputChange('preiscrizione', e.target.checked)}
                    disabled={isSubmitting}
                  />
                  <i className="fas fa-user-plus"></i>
                  Preiscrizione
                </label>
                <small className="checkbox-description">
                  Rimuove il limite minimo di 7 giocatori per l'iscrizione
                </small>
              </div>

              <div className="form-group"></div>
            </div>

            {/* Errore di submit */}
            {errors.submit && (
              <div className="error-message" style={{ marginTop: '1rem', gridColumn: '1 / -1' }}>
                <i className="fas fa-exclamation-triangle"></i>
                {errors.submit}
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            type="button"
            className="modal-btn modal-btn-cancel"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <i className="fas fa-times"></i>
            Annulla
          </button>
          <button
            type="submit"
            className="modal-btn modal-btn-primary"
            onClick={handleSubmit}
            disabled={isSubmitting || !formData.torneo || !formData.edizione.trim() || !formData.luogo.trim()}
          >
            {isSubmitting ? (
              <>
                <div className="modal-loading-spinner"></div>
                {editingEdizione ? 'Salvataggio...' : 'Creazione...'}
              </>
            ) : (
              <>
                <i className={`fas ${editingEdizione ? 'fa-save' : 'fa-plus'}`}></i>
                {editingEdizione ? 'Salva Modifiche' : 'Crea Edizione'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateEdizioneModal;